import { useEffect } from 'react';
import { EmiFlowType, WaiveFeeFormProps, WaiveFeeType } from '../../interfaces/EmiSchedule.type';
import styles from './index.module.scss';
import cx from 'classnames';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import {
  WAIVE_FEE_DURATION,
  WAIVE_FEE_OPTIONS,
  WaiveFeeActions
} from '../../constants/EmiSchedule.component';
import { formatAmount } from 'src/utils/commonUtils';
import BorderedInput from '@navi/web-ui/lib/primitives/BorderedInput/BorderedInput';
import TextArea from '@navi/web-ui/lib/primitives/TextArea/TextArea';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import { ErrorTexts } from '../../constants/EmiSchedule.constant';
import Dropdown from 'src/components/Dropdown';
import { SelectPickerOptionProps } from 'src/components/interfaces';

const WaiveFeeForm = ({
  pendingFeeComponent,
  register,
  errors,
  watch,
  index,
  resetField,
  calculateTotalWaiverAmount,
  setValue,
  flowType,
  reset,
  dueDetails
}: WaiveFeeFormProps) => {
  const {
    feeType,
    totalOriginalFeeAmount,
    totalOriginalFeeGst,
    totalRevisedFeeAmount,
    totalRevisedFeeGst
  } = pendingFeeComponent;

  const defaultAmtValue = dueDetails?.unpaidPenalty;

  const actionType = watch(`componentAdjustments.${index}.requestType`);
  const isFormOpen = true;
  const waiverAmount = watch(`componentAdjustments.${index}.waiverAmount.value`);
  const comment = watch(`componentAdjustments.${index}.remark`);
  const duration = watch(`componentAdjustments.${index}.duration`);

  useEffect(() => {
    setValue(`componentAdjustments.${index}.feeType`, feeType);
    setValue(`componentAdjustments.${index}.requestType`, WAIVE_FEE_OPTIONS[0].value as string);
    setValue(`componentAdjustments.${index}.duration`, WAIVE_FEE_DURATION[0].value as string);
    setValue(`componentAdjustments.${index}.waiverAmount.value`, defaultAmtValue);
    return () => reset();
  }, []);

  useEffect(() => {
    if (waiverAmount) {
      calculateTotalWaiverAmount(defaultAmtValue);
    }
  }, [waiverAmount, isFormOpen, actionType]);

  const actionDropdownHandler = (action: SelectPickerOptionProps) => {
    setValue(`componentAdjustments.${index}.requestType`, action.value as string, {
      shouldValidate: true
    });
    if (action.value === WaiveFeeActions.HOLD.name) {
      setValue(`componentAdjustments.${index}.duration`, WAIVE_FEE_DURATION[0].value as string);
    } else {
      resetField(`componentAdjustments.${index}.duration`);
    }
  };

  const durationDropdownHandler = (duration: SelectPickerOptionProps) => {
    setValue(`componentAdjustments.${index}.duration`, duration.value as string, {
      shouldValidate: true
    });
  };

  const validateMultipleOf50 = (value: number) => {
    if (flowType === EmiFlowType.NEW_FLOW) {
      const lowerValue = value - (value % 50);
      const upperValue = value - (value % 50) + 50;
      if (value && value % 50 !== 0) {
        return `Nearest Multiples of 50 are ${lowerValue} and ${upperValue}`;
      }
    }
  };

  const renderFormContent = () => {
    return (
      <>
        <div className={styles.actionContainer}>
          <div className={styles.dropdownContainer}>
            <Typography variant={'p4'} as={'p'}>
              Action {<RequiredIcon className={styles.requiredIcon} />}
            </Typography>
            <Dropdown
              selectedValue={actionType}
              options={WAIVE_FEE_OPTIONS}
              onSelectionChange={actionDropdownHandler}
              {...register(`componentAdjustments.${index}.requestType`, { required: true })}
              placeholder="Select action"
              containerClasses={styles.dropdown}
            />
          </div>
          <div className={styles.inputContainer}>
            <BorderedInput
              inputLabel="Enter amount"
              placeholder="Enter amount"
              type="number"
              containerClassName={styles.formInput}
              {...register(`componentAdjustments.${index}.waiverAmount.value`, {
                valueAsNumber: true,
                max: { value: defaultAmtValue, message: ErrorTexts.MAX },
                min: { value: 50, message: ErrorTexts.MIN },
                required: {
                  value: actionType === WaiveFeeActions.WAIVER.name,
                  message: ErrorTexts.REQUIRED
                },
                validate: validateMultipleOf50
              })}
              disabled={flowType === EmiFlowType.OLD_FLOW}
              step={50}
              value={waiverAmount}
              error={errors.componentAdjustments?.[index]?.waiverAmount?.value?.message}
            />
          </div>
        </div>

        <div
          className={cx(styles.dropdownContainer, styles.marginTop, {
            [styles.hide]: actionType !== WaiveFeeActions.HOLD.name
          })}
        >
          <Typography variant={'p4'} as={'p'}>
            Duration {<RequiredIcon />}
          </Typography>
          <Dropdown
            selectedValue={duration}
            options={WAIVE_FEE_DURATION}
            onSelectionChange={durationDropdownHandler}
            {...register(`componentAdjustments.${index}.duration`, {
              required: actionType === WaiveFeeActions.HOLD.name
            })}
            disabled={actionType !== WaiveFeeActions.HOLD.name}
            placeholder="Select duration"
            containerClasses={styles.dropdown}
          />
        </div>

        <div className={cx(styles.inputContainer, styles.marginTop)}>
          <TextArea
            label="Comments"
            placeholder="Enter here"
            {...register(`componentAdjustments.${index}.remark`, {
              required: ErrorTexts.REQUIRED
            })}
            containerClassName={cx(styles.formInput, styles.formInputSize)}
            error={errors.componentAdjustments?.[index]?.remark?.message}
            value={comment}
            required
          />
        </div>
        <div className={styles.revisedFeeContainer}>
          <Typography variant="p5" as="p" className={styles.revisedFeeContainer__text}>
            Revised fee incl. GST
          </Typography>
          <Typography variant="p5" as="p" className={styles.revisedFeeContainer__amt}>
            {formatAmount(defaultAmtValue - (waiverAmount || 0), false)}
          </Typography>
        </div>
      </>
    );
  };

  return (
    <div className={styles.waiveFormContainer}>
      <div className={cx(styles.header)}>
        <div>
          <Typography variant="p3" className={styles.header__text}>
            {WaiveFeeType[feeType]}
          </Typography>
          <Typography variant="p5" className={styles.header__subText}>
            {formatAmount(defaultAmtValue, false)} incl. GST
          </Typography>
        </div>
        {/* <Switch {...register(`componentAdjustments.${index}.isFormOpen`)} id={'waive-fee-switch'} /> */}
      </div>
      <div
        className={cx(styles.formWrapper, {
          [styles['formWrapper--open']]: isFormOpen
        })}
      >
        <div>
          <div className={cx(styles.content)}>{isFormOpen && renderFormContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default WaiveFeeForm;
